import {
  ItemSetQuestionPropertiesLocationType,
  QBankStructureLevelCategory,
  QConfigStructureCategory,
  type GetItemSetQuestionPropertiesCountByLocationQuery,
  type QConfigStructureType,
} from '@exam-management/graphql';
import { type OptionSelectionProps } from '@shared/components';
import { type UseFormSetError } from 'react-hook-form';

import { type TableData } from './components';
import { type BaseQuestionConditionsContextType } from './types';

const QUESTION_TYPE_CATEGORIES = [
  QConfigStructureCategory.Single,
  QConfigStructureCategory.ItemSet,
];

const CATEGORY_ORDERING = [
  QConfigStructureCategory.Course,
  QConfigStructureCategory.Part,
  QConfigStructureCategory.Subject,
  QConfigStructureCategory.Group,
  QConfigStructureCategory.Chapter,
  QConfigStructureCategory.Single,
  QConfigStructureCategory.ItemSet,
  QConfigStructureCategory.LearningObjectiveStatement,
  QConfigStructureCategory.Topic,
  QConfigStructureCategory.SubTopic,
  QConfigStructureCategory.AllSubTopic,
];

export const CONTENT_TYPE_TEXT_MAP = {
  TOPIC: 'Topic',
  LOS: 'LOS',
};

export function removeStructuresAndItsChildrenByQuestionType<
  T extends {
    id: string;
    category: QConfigStructureCategory | null;
    type: QConfigStructureType;
    parentId: string | null;
    order: number;
    size: number | null;
  },
>(input: {
  structures: T[];
  removeTargetCategory: QConfigStructureCategory;
  questionSize?: number;
}): T[] {
  const { structures, removeTargetCategory, questionSize } = input;

  if (!QUESTION_TYPE_CATEGORIES.includes(removeTargetCategory)) {
    throw new Error('Category must be Single or ItemSet');
  }

  const sortedStructures = [...structures].sort((a, b) => {
    if (a.category && b.category && a.category !== b.category) {
      return CATEGORY_ORDERING.indexOf(a.category) - CATEGORY_ORDERING.indexOf(b.category);
    }
    return a.order - b.order;
  });

  const toRemove = new Set<string>();

  for (const structure of sortedStructures) {
    const categoryMatched =
      structure.category !== null &&
      QUESTION_TYPE_CATEGORIES.includes(structure.category) &&
      structure.category === removeTargetCategory;
    const sizeMatched = questionSize === structure.size;

    const removeConditionByCategoryAndSize =
      categoryMatched && (questionSize ? !sizeMatched : true);
    const removeConditionByParent = structure.parentId !== null && toRemove.has(structure.parentId);

    if (
      (removeConditionByCategoryAndSize || removeConditionByParent) &&
      !toRemove.has(structure.id)
    ) {
      toRemove.add(structure.id);
    }
  }

  return structures.filter((s) => !toRemove.has(s.id));
}

export function checkIfCombinationExists(
  existingLOS: string[][],
  selectedLOS: OptionSelectionProps[],
  option?: OptionSelectionProps | null,
) {
  return existingLOS.some((existing) => {
    const existingSet = new Set(existing);
    const selectedSet = new Set(selectedLOS.map((selected) => selected.value));
    if (option?.value) {
      selectedSet.add(option.value);
    }

    return (
      existingSet.size === selectedSet.size &&
      [...existingSet].every((value) => selectedSet.has(value))
    );
  });
}

export function getTopicAndSubtopicFormValues(
  input: Pick<
    BaseQuestionConditionsContextType,
    'structures' | 'levelItemsWithLevel' | 'checkExistOnStructures' | 'chapterData'
  > & {
    questionTypeStructureId?: string;
  },
) {
  const {
    questionTypeStructureId,
    structures,
    levelItemsWithLevel,
    checkExistOnStructures,
    chapterData,
  } = input;

  const questionTypeChildren = structures.filter(
    (structure) => structure.parentId === questionTypeStructureId,
  );
  const questionTypeGrandChildren = structures.filter((structure) =>
    questionTypeChildren.some((child) => child.id === structure.parentId),
  );

  const chapterLevelItem = levelItemsWithLevel.find(
    (item) => item.level.category === QBankStructureLevelCategory.Chapter,
  );

  let topicLevelItems = levelItemsWithLevel.filter(
    (item) => item.level.category === QBankStructureLevelCategory.Topic,
  );
  if (topicLevelItems.length === 0 && chapterLevelItem) {
    topicLevelItems = [chapterLevelItem];
  }

  const subTopicLevelItems = levelItemsWithLevel.filter(
    (item) => item.level.category === QBankStructureLevelCategory.SubTopic,
  );

  let hasAllSubtopicWithNoTopic = false;
  const topicRefIds = questionTypeChildren
    .filter((s) => s.refId !== null)
    .map((structure) => structure.refId as string);
  const subtopicRefIds = questionTypeGrandChildren.reduce<string[]>((acc, structure) => {
    if (structure.refId !== null) {
      acc.push(structure.refId);
    }

    if (structure.category === QConfigStructureCategory.AllSubTopic) {
      let parentRefId = questionTypeChildren.find((item) => item.id === structure.parentId)?.refId;
      if (!parentRefId && chapterData?.refId) {
        parentRefId = chapterData.refId;
        hasAllSubtopicWithNoTopic = true;
      }

      const allSubtopics = levelItemsWithLevel.filter(
        (item) => item.parentLevelItemId === parentRefId,
      );
      for (const subtopic of allSubtopics) {
        if (!acc.includes(subtopic.id)) {
          acc.push(subtopic.id);
        }
      }
    }

    return acc;
  }, []);

  // check all subtopics
  const topics: Record<string, boolean> = topicLevelItems.reduce((acc, item) => {
    if (
      topicRefIds.includes(item.id) ||
      (hasAllSubtopicWithNoTopic && chapterData?.refId === item.id)
    ) {
      return {
        ...acc,
        [item.id]: true,
      };
    }

    const children = subTopicLevelItems.filter((subItem) => subItem.parentLevelItemId === item.id);
    const isAllSelected = children.every((child) => checkExistOnStructures(child.id));
    return {
      ...acc,
      [item.id]: isAllSelected ? undefined : false,
    };
  }, {});

  if (!topicLevelItems.length && chapterData?.refId) {
    topics[chapterData.refId] = subTopicLevelItems
      .filter((subItem) => subItem.parentLevelItemId === chapterData.refId)
      .every((child) => checkExistOnStructures(child.id));
  }

  //  check selected
  const subtopics: Record<string, boolean> = subTopicLevelItems.reduce((acc, item) => {
    const isAlreadySelected = checkExistOnStructures(item.id);
    if (subtopicRefIds.includes(item.id)) {
      return {
        ...acc,
        [item.id]: true,
      };
    }

    return {
      ...acc,
      [item.id]: isAlreadySelected ? undefined : false,
    };
  }, {});

  return { topics, subtopics };
}

// ----------- Item Set Formatters -----------

export function formatTopicsOrSubtopics<T extends Record<string, boolean | null | undefined>>(
  input: T | undefined,
) {
  if (!input) return [];

  return Object.entries(input ?? {})
    .filter(([, v]) => v)
    .map(([k]) => k);
}

interface GenericCondition {
  needTotal: number;
  properties: {
    difficulty: {
      value: string;
      label: string;
    };
    calculated: {
      value: string;
      label: string;
    };
  }[];
}

export function formatConditions<T extends GenericCondition>(conditions: Array<T | null>) {
  return conditions.map((cond) => ({
    needTotal: cond?.needTotal ?? 0,
    tagGroups: groupTagProperties(cond?.properties ?? []),
  }));
}

function groupTagProperties<T extends Array<Record<string, { value: string }>>>(properties: T) {
  const tagGroupMap = properties.reduce((map, prop) => {
    const tagIds = Object.values(prop).map((p) => p.value);

    const key = tagIds.join('|');
    map.set(key, { tagIds, count: (map.get(key)?.count ?? 0) + 1 });
    return map;
  }, new Map<string, { tagIds: string[]; count: number }>());

  return Array.from(tagGroupMap.values());
}

export function getLocationTypeByContentType(contentType: 'TOPIC' | 'LOS') {
  if (contentType === 'TOPIC') {
    return ItemSetQuestionPropertiesLocationType.QbankStructureLevelItem;
  }

  return ItemSetQuestionPropertiesLocationType.LearningObjectiveStatement;
}

interface Condition {
  totalAvailableQuestions: number;
  needTotal: number;
  tagGroups: {
    tags: {
      id: string;
      key: string;
      type: 'difficulty' | 'calculated';
      index: number[];
    }[];
    count: number;
  }[];
  index: number;
}

export function getCombinationKey(
  questions: { difficulty: { value: string }; calculated: { value: string } }[],
): string {
  return questions
    .map((q) => `${q.difficulty.value}-${q.calculated.value}`)
    .sort((a, b) => a.localeCompare(b))
    .join('|');
}

function getKeyFromProperties(input: { difficultyId: string; calculatedId: string }) {
  return `${input.calculatedId}_${input.difficultyId}`;
}

function formatTagGroups<
  T extends {
    count: number;
    tags: Array<{
      id: string;
    }>;
  },
>(tagGroups?: T[]) {
  return (
    tagGroups
      ?.flatMap((group) =>
        group.tags.map((tag: { id: string }) => {
          return {
            id: tag.id,
            count: group.count,
          };
        }),
      )
      .sort((a, b) => a.id.localeCompare(b.id) || b.count - a.count) ?? []
  );
}

export function validateItemSetConditions<T extends GenericCondition>(input: {
  conditions: Array<T | null>;
  queryResult?: GetItemSetQuestionPropertiesCountByLocationQuery['qctGetItemSetQuestionPropertiesCountByLocation'];
}) {
  const { conditions, queryResult } = input;

  let totalValidQuestions = 0;
  let allQueriedQuestions = 0;

  const countedFormConditions = conditions
    .filter((condition): condition is T => condition !== null)
    .map((condition, index) => {
      const { properties, needTotal } = condition;

      const propertyCountMap = new Map<string, number>();
      const propertyKeyById = new Map<string, string>();
      const indexesByPropertyKey = new Map<string, number[]>();

      for (const prop of properties) {
        const difficultyValue: string | undefined = prop.difficulty?.value;
        const calculatedValue: string | undefined = prop.calculated?.value;

        if (!difficultyValue || !calculatedValue) {
          continue;
        }

        const key = getKeyFromProperties({
          difficultyId: difficultyValue,
          calculatedId: calculatedValue,
        });

        if (!propertyKeyById.has(difficultyValue)) {
          propertyKeyById.set(difficultyValue, prop.difficulty.label);
        }

        if (!propertyKeyById.has(prop.calculated.value)) {
          propertyKeyById.set(calculatedValue, prop.calculated.label);
        }

        propertyCountMap.set(key, (propertyCountMap.get(key) ?? 0) + 1);

        if (!indexesByPropertyKey.has(key)) {
          indexesByPropertyKey.set(key, []);
        }
        indexesByPropertyKey.get(key)?.push(properties.indexOf(prop));
      }

      const tagGroups = Array.from(propertyCountMap.entries()).map(([key, count]) => {
        const [calculatedId, difficultyId] = key.split('_');
        return {
          tags: [
            {
              id: calculatedId,
              key: propertyKeyById.get(calculatedId) ?? '',
              type: 'calculated' as const,
              index: indexesByPropertyKey.get(key) ?? [],
            },
            {
              id: difficultyId,
              key: propertyKeyById.get(difficultyId) ?? '',
              type: 'difficulty' as const,
              index: indexesByPropertyKey.get(key) ?? [],
            },
          ],
          count,
        };
      });

      return {
        needTotal,
        tagGroups,
        index,
      };
    });

  const invalidConditions: Condition[] = [];

  const queriedPropertiesCount = queryResult ?? [];

  const formattedFormConditions = countedFormConditions.map((condition) => {
    const matchedQueriedTagGroups = queriedPropertiesCount.find((item) => {
      const conditionTags = formatTagGroups(condition.tagGroups);
      const itemTagIds = formatTagGroups(item.tagGroups);

      return (
        conditionTags.length === (itemTagIds?.length ?? 0) &&
        conditionTags.every((tag, index) => {
          return tag.id === itemTagIds?.[index].id && tag.count === itemTagIds?.[index].count;
        })
      );
    });

    const formattedCondition = {
      ...condition,
      totalAvailableQuestions: matchedQueriedTagGroups?.totalQuestions ?? 0,
    };

    if (!matchedQueriedTagGroups || matchedQueriedTagGroups.totalQuestions < condition.needTotal) {
      invalidConditions.push(formattedCondition);
      allQueriedQuestions += matchedQueriedTagGroups?.totalQuestions ?? 0;
    } else if (matchedQueriedTagGroups.totalQuestions >= condition.needTotal) {
      totalValidQuestions += condition.needTotal;
      allQueriedQuestions += matchedQueriedTagGroups.totalQuestions;
    }

    return formattedCondition;
  });

  return {
    isValid: invalidConditions.length === 0,
    results: {
      invalidConditions,
      validConditions: formattedFormConditions,
      totalValidQuestions,
      allQueriedQuestions,
    },
  };
}

export function setErrorsByInvalidConditions(
  invalidConditions: Condition[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setError: UseFormSetError<any>,
) {
  for (const invalidCondition of invalidConditions) {
    const { index, needTotal, totalAvailableQuestions, tagGroups } = invalidCondition;

    if (totalAvailableQuestions === 0) {
      setError(`conditions.${index}`, {
        type: 'manual',
        message: `ไม่พบข้อสอบที่ตรงตามเงื่อนไข`,
      });

      for (const group of tagGroups) {
        for (const tag of group.tags) {
          for (const propertyIndex of tag.index) {
            setError(`conditions.${index}.properties.${propertyIndex}.${tag.type}`, {
              type: 'manual',
              message: '',
            });
          }
        }
      }
    } else if (totalAvailableQuestions < needTotal) {
      setError(`conditions.${index}.needTotal`, {
        type: 'manual',
        message: 'จำนวนข้อสอบไม่เพียงพอ',
      });
    }
  }
}

export function formatTableData(conditions: Array<Condition>): Array<{
  data: TableData;
  total: number;
}> {
  const diffMap: Record<string, keyof TableData['calculated']> = {
    ง่าย: 'easy',
    กลาง: 'medium',
    ยาก: 'hard',
  };
  const calcMap: Record<string, keyof TableData> = {
    คำนวณ: 'calculated',
    ไม่คำนวณ: 'notCalculated',
  };

  const formattedTableData: {
    data: TableData;
    total: number;
  }[] = conditions.map((condition) => {
    const table: TableData = { calculated: {}, notCalculated: {} };

    if (condition.tagGroups) {
      for (const prop of condition.tagGroups) {
        const diffKey = diffMap[prop.tags.find((tag) => diffMap[tag.key])?.key ?? ''];
        const calcKey = calcMap[prop.tags.find((tag) => calcMap[tag.key])?.key ?? ''];
        if (diffKey && calcKey) {
          table[calcKey][diffKey] = prop.count;
        }
      }
    }

    return {
      data: table,
      total: condition.totalAvailableQuestions,
    };
  });

  return formattedTableData;
}

export function calculateSummaryData(input: {
  itemSetType: number;
  allQueriedQuestions: number;
  totalValidQuestions: number;
  scorePerQuestion: number;
}) {
  const { itemSetType, allQueriedQuestions, totalValidQuestions, scorePerQuestion } = input;

  const totalItemSetQuestions = allQueriedQuestions
    ? `${allQueriedQuestions}(${allQueriedQuestions * itemSetType})`
    : '-';
  const totalScore = totalValidQuestions * itemSetType * scorePerQuestion;
  const selectedQuestions = totalValidQuestions;

  return { totalItemSetQuestions, totalScore, selectedQuestions };
}

export function validateLOSSelectionAndSetError(
  selectedLOS: OptionSelectionProps[],
  existingLOS: string[][],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setError: UseFormSetError<any>,
) {
  const checkExistingResult = checkIfCombinationExists(existingLOS, selectedLOS);

  if (checkExistingResult) {
    setError('los', {
      type: 'manual',
      message: 'กรุณาเลือกเงื่อนไขที่ไม่ซ้ำกับรายการที่มีอยู่แล้ว',
    });
  }

  return !checkExistingResult;
}
